import { useCallback, useEffect, useMemo, useState } from "react"
import Select from "react-select"
import DatePicker from "react-datepicker"
import moment from "moment"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"

const service = new UserService()

const TAG_OPTIONS = [
  "Rep",
  "Recruiter",
  "Team Lead",
  "Manager",
  "Senior Manager",
  "Regional",
  "Senior Regional",
  "Partner",
  "Division",
  "Office",
]

const TENURE_OPTIONS = ["All", "Rookie", "Veteran", "Sophomore"]

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
  formatValue,
}: {
  value: number
  maxVal: number
  colorClass?: string
  formatValue?: (v: number) => string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  const display = formatValue ? formatValue(value) : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <span className="text-sm tabular-nums shrink-0 text-ink w-12 text-right">{display}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[50px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

type MobileRow = {
  name: string
  efp: number
  efp_old: number
  fp: number
  avg: number
  third: number
  ic: number
  prs: number
  pc: number
  cr: number
  fp_thirdlook: number
}

type MobileFilterResponse = {
  success: boolean
  data: MobileRow[]
  total_efp: number
  total_fp: number
  total_efp_old: number
  total_prs: number
  total_pc: number
  total_cr: number
  total_avg: number
  total_third: number
  total_ic: number
  total_fp_third: number
}

export const MobileReport = () => {
  const [date, setDate] = useState<string>("LASTWEEK")
  const [showCustom, setShowCustom] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [tag, setTag] = useState<string>("Senior Regional")
  const [tenure, setTenure] = useState<string>("All")
  const [userNames, setUserNames] = useState<string[]>([])
  const [usersFromServer, setUsersFromServer] = useState<{ name: string; badgeid: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<MobileFilterResponse | null>(null)

  const userOptions = useMemo(
    () =>
      usersFromServer.map((u) => ({
        value: u.name,
        label: u.name,
      })),
    [usersFromServer]
  )
  const selectedUsers = useMemo(
    () => userOptions.filter((o) => userNames.includes(o.value)),
    [userOptions, userNames]
  )

  const fetchUsers = useCallback(() => {
    if (!tag) return
    service
      .mobileUsersByPosition(tag)
      .then((resp: any) => {
        const data = resp?.data?.data ?? resp?.data
        if (Array.isArray(data)) {
          setUsersFromServer(data.map((d: any) => ({ name: d.name ?? d.badgeid, badgeid: d.badgeid ?? d.name })))
        } else {
          setUsersFromServer([])
        }
      })
      .catch(() => setUsersFromServer([]))
  }, [tag])

  const fetchReport = useCallback(() => {
    if (!date || !tag) return
    setLoading(true)
    const usersParam = userNames.length > 0 ? userNames.join(",") : "All"
    service
      .mobileFilter({ date, tag, tenure, users: usersParam })
      .then((resp: any) => {
        if (resp?.data?.success !== false && (resp?.data?.data || resp?.data)) {
          setResponse(resp.data)
        } else {
          setResponse(null)
        }
      })
      .catch(() => setResponse(null))
      .finally(() => setLoading(false))
  }, [date, tag, tenure, userNames])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    if (date && tag) fetchReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start ?? null)
    setEndDate(end ?? null)
    if (start && end) {
      setDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  const tableRows = response?.data ?? []
  const maxEfp = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.efp) || 0), 1) : 1
  const maxEfpOld = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.efp_old) || 0), 1) : 1
  const maxFp = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.fp) || 0), 1) : 1
  const maxAvg = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.avg) || 0), 1) : 1
  const maxThird = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.third) || 0), 1) : 1
  const maxIc = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.ic) || 0), 1) : 1
  const maxPrs = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.prs) || 0), 1) : 1
  const maxPc = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.pc) || 0), 1) : 1
  const maxCr = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.cr) || 0), 1) : 1
  const maxFpThird = tableRows.length ? Math.max(...tableRows.map((r) => Number(r.fp_thirdlook) || 0), 1) : 1

  const totals = response
    ? {
        efp: response.total_efp ?? 0,
        fp: response.total_fp ?? 0,
        efp_old: response.total_efp_old ?? 0,
        prs: response.total_prs ?? 0,
        pc: response.total_pc ?? 0,
        cr: response.total_cr ?? 0,
        avg: response.total_avg ?? 0,
        third: response.total_third ?? 0,
        ic: response.total_ic ?? 0,
        fp_third: response.total_fp_third ?? 0,
      }
    : null

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Mobile Report</h1>
        <p className="page-subtitle">Production metrics by position (date, tenure, users).</p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Date</label>
            <select
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") {
                  setDate(e.target.value)
                } else {
                  setShowCustom(true)
                }
              }}
              className="px-3 py-2 text-sm"
            >
              <option value="">Select</option>
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showCustom && (
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChangeDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-60 rounded-lg border border-line bg-white shadow-card-lg text-ink px-3 text-sm"
                customInput={
                  <input
                    placeholder="Select date"
                    className="w-60 py-2 px-3 text-sm"
                  />
                }
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="field-label">Position (tag)</label>
            <select
              value={tag}
              onChange={(e) => {
                setTag(e.target.value)
                setUserNames([])
              }}
              className="px-3 py-2 text-sm"
            >
              {TAG_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Tenure</label>
            <select
              value={tenure}
              onChange={(e) => setTenure(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {TENURE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[260px]">
            <label className="field-label">Users (optional; leave empty for all)</label>
            <Select
              isMulti
              isSearchable
              placeholder="All..."
              value={selectedUsers}
              options={userOptions}
              onChange={(selected) => setUserNames((selected || []).map((s) => s.value))}
              onMenuOpen={fetchUsers}
              className="react-select min-w-[240px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                multiValue: (base) => ({ ...base, backgroundColor: "#f1f3f5" }),
                multiValueLabel: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!date || !tag || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="card-title">Results by {tag}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Name</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">EFP</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">EFP Old</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">FP</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Avg</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Third</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">IC</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">PRS</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">PC</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">CR</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">FP Third</th>
              </tr>
            </thead>
            <tbody>
              {loading && !response ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-ink-muted">
                    {response ? "No data for the selected filters." : "Select date and position, then click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row, idx) => (
                  <tr key={row.name ?? idx} className="border-b border-line hover:bg-surface transition-colors">
                    <td className="px-2 py-3 text-ink text-sm whitespace-nowrap">{row.name}</td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.efp) || 0} maxVal={maxEfp} formatValue={(v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 })} />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.efp_old) || 0} maxVal={maxEfpOld} colorClass="bg-gray-500" formatValue={(v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 })} />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.fp) || 0} maxVal={maxFp} colorClass="bg-emerald-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.avg) || 0} maxVal={maxAvg} colorClass="bg-amber-500" formatValue={(v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 })} />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.third) || 0} maxVal={maxThird} colorClass="bg-violet-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.ic) || 0} maxVal={maxIc} colorClass="bg-cyan-500" formatValue={(v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 })} />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.prs) || 0} maxVal={maxPrs} colorClass="bg-indigo-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.pc) || 0} maxVal={maxPc} colorClass="bg-teal-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.cr) || 0} maxVal={maxCr} colorClass="bg-rose-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={Number(row.fp_thirdlook) || 0} maxVal={maxFpThird} colorClass="bg-sky-500" formatValue={(v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 })} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                  <td className="px-2 py-3 whitespace-nowrap">Total</td>
                  <td className="px-2 py-3">{Number(totals.efp).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-2 py-3">{Number(totals.efp_old).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-2 py-3">{totals.fp}</td>
                  <td className="px-2 py-3">{Number(totals.avg).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-2 py-3">{totals.third}</td>
                  <td className="px-2 py-3">{Number(totals.ic).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-2 py-3">{totals.prs}</td>
                  <td className="px-2 py-3">{totals.pc}</td>
                  <td className="px-2 py-3">{totals.cr}</td>
                  <td className="px-2 py-3">{totals.fp_third}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
